import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ImageProcessingResult {
  success: boolean;
  storage_image_url?: string;
  storage_image_path?: string;
  error?: string;
}

export class RecipeImageService {
  
  /**
   * Process a recipe image URL by downloading, processing, and uploading to storage
   */
  static async processRecipeImage(
    recipeId: string, 
    imageUrl: string
  ): Promise<ImageProcessingResult> {
    try {
      console.log(`🖼️ Starting image processing for recipe ${recipeId} with URL: ${imageUrl}`);
      
      const { data, error } = await supabase.functions.invoke('process-recipe-image', {
        body: {
          recipeId,
          imageUrl
        }
      });

      if (error) {
        console.error('❌ Edge function error:', error);
        return {
          success: false,
          error: 'Failed to process image'
        };
      }

      if (!data.success) {
        console.error('❌ Image processing failed:', data.error);
        return {
          success: false,
          error: data.error || 'Image processing failed'
        };
      }

      console.log('✅ Image processing successful:', data.storage_image_url);
      return {
        success: true,
        storage_image_url: data.storage_image_url,
        storage_image_path: data.storage_image_path
      };

    } catch (error) {
      console.error('❌ Recipe image processing error:', error);
      return {
        success: false,
        error: 'Failed to process recipe image'
      };
    }
  }

  /**
   * Check if an image URL is valid
   */
  static isValidImageUrl(url: string): boolean {
    if (!url) return false;
    
    try {
      const urlObj = new URL(url);
      const validProtocols = ['http:', 'https:'];
      const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
      
      return validProtocols.includes(urlObj.protocol) &&
             validExtensions.some(ext => url.toLowerCase().includes(ext));
    } catch {
      return false;
    }
  }

  /**
   * Check if an image URL contains an unsupported format
   */
  static getImageFormatWarning(url: string): string | null {
    if (!url) return null;
    
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.heic') || urlLower.includes('.heif')) {
      return 'HEIC/HEIF format detected. This format is not supported. Please convert to JPG, PNG, or WebP.';
    }
    
    const supportedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    const hasKnownExtension = supportedExtensions.some(ext => urlLower.includes(ext));
    
    if (!hasKnownExtension) {
      return 'Unknown image format. Supported formats: JPG, PNG, WebP, GIF';
    }
    
    return null;
  }
}
